import type { Exam } from '../domain/types';
import type { HydrationResult } from '../persistence/hydrate';
import type { Sheet } from './sheet';

/**
 * Der Startablauf: was die App beim Hochkommen als Nächstes von ihrem Benutzer braucht.
 *
 * Liegt aus demselben Grund neben `sheet.ts` in einer eigenen Datei: `hydrate.ts` und der Store
 * hängen beide daran, aber nicht aneinander.
 *
 * ```
 * wiedereinstieg ──ja──► bereit
 *        └─nein─┐
 *               ▼
 *           auswahl ◄── abbrechen (verwirft die halbe Wahl)
 *               │ Prüfung + Station + Prüfer stehen
 *               ▼
 *           pruefling
 *               │ QR oder Liste
 *               ▼
 *            bereit
 * ```
 *
 * ⚠️ **Der gemerkte Stand wohnt im Startzustand, nicht im Store.** Solange die Frage nach dem
 * Wiedereinstieg offen ist, sind `examId`, `stationId` und `candidateId` im Store leer. Das ist
 * Absicht und der Grund, warum eine einzige Wache in `autosave.ts` genügt: es gibt keinen
 * Zwischenzustand, in dem eine Kennung steht, die niemand gewählt oder bestätigt hat.
 */
export type Startup =
  /**
   * Es lag eine angefangene Bewertung vor. Der Prüfer entscheidet, ob er sie fortsetzt.
   *
   * `sheet` ist das gemerkte offene Blatt. Es wartet hier, statt schon im Store zu stehen:
   * ginge es vor der Antwort auf, läge es über der Frage.
   */
  | { kind: 'wiedereinstieg'; stand: GemerkterStand; sheet: Sheet }
  /** Prüfung, Station, Prüfer (und wahlweise Schauspieler) wählen. */
  | { kind: 'auswahl' }
  /** Alles steht ausser dem Prüfling. */
  | { kind: 'pruefling' }
  /** Bewerten ist möglich. Nur in diesem Zustand schreibt die Sicherung. */
  | { kind: 'bereit'; stand: GemerkterStand };

export interface GemerkterStand {
  examId: string;
  stationId: string;
  candidateId: string;
  examinerId: string;
  actorId: string;
}

/**
 * Womit die App hochkommt.
 *
 * Gefragt wird nur, wenn der gemerkte Prüfling **noch existiert** und seine Bewertung
 * **wirklich angefangen** ist. Beides ist nötig: eine Prüfung kann seit dem letzten Start neu
 * heruntergeladen worden sein (dann zeigt die gemerkte Kennung ins Leere), und nach einem
 * regulären Abschluss wäre die Frage „weitermachen?" sinnlos — da steht der nächste Prüfling
 * vor der Tür, nicht der letzte.
 */
export function startzustand(result: HydrationResult, exam: Exam): Startup {
  const c = result.client;
  const candidateId = c.currentCandidateID ?? '';
  if (!candidateId) return { kind: 'auswahl' };
  if (!exam.candidates.some((k) => k.id === candidateId)) return { kind: 'auswahl' };

  // ⚠️ **Nicht am Vorhandensein der Bewertung festmachen.** `loadAssessment` liefert immer
  // ein Objekt, notfalls ein leeres — die Prüfung `if (!result.assessment)` traf deshalb nie
  // zu, und die App fragte nach jedem gewöhnlichen Kaltstart nach dem Weitermachen, auch bei
  // einem längst abgegebenen Prüfling. Der belastbare Hinweis ist der Zustand aus Relay:
  // `RUNNING` heißt, der Prüfling ist an der Station und noch nicht fertig, `PAUSED` ebenso.
  const status = result.statuses[candidateId];
  if (status !== 'RUNNING' && status !== 'PAUSED') return { kind: 'auswahl' };

  // Ohne bekannte Station lässt sich nichts wiederherstellen — dann lieber neu wählen als
  // eine raten. Der Fall ist in der Praxis ausgeschlossen: `CONTAINER_START` wird bei jeder
  // Prüflingswahl geschrieben, bevor überhaupt etwas bewertet werden kann.
  const stationId = result.lastStationId;
  if (!stationId || !exam.stations.some((st) => st.id === stationId)) return { kind: 'auswahl' };

  return {
    kind: 'wiedereinstieg',
    sheet: result.sheet,
    stand: {
      examId: exam.id,
      stationId,
      candidateId,
      examinerId: c.currentExaminerID ?? '',
      actorId: c.currentActorID ?? '',
    },
  };
}
